import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { attributionColumns, attributionFields } from "@/lib/attribution-schema";

const executorSchema = z.object({
  name: z.string().trim().min(2, "Please tell us your name").max(120),
  email: z.string().trim().email("Please enter a valid email address").max(200),
  phone: z.string().trim().max(40).optional().or(z.literal("")),
  role: z.enum(["named-executor", "next-of-kin", "beneficiary", "adviser", "unsure"]),
  deceasedName: z.string().trim().max(160).optional().or(z.literal("")),
  dateOfDeath: z.string().trim().max(40).optional().or(z.literal("")),
  placeOfDeath: z.enum(["qld", "interstate", "overseas", "unsure"]).default("qld"),
  applicantLocation: z.enum(["qld", "interstate", "overseas", "unsure"]).default("qld"),
  qldAssets: z.array(z.string().trim().max(60)).max(12).optional(),
  willStatus: z.enum(["original-held-by-me", "original-with-firm", "copy-only", "no-will", "unsure"]),
  willLocation: z.string().trim().max(250).optional().or(z.literal("")),
  assets: z.array(z.string().trim().max(60)).max(12).optional(),
  estimatedValue: z.enum(["under-100k", "100k-500k", "500k-1m", "1m-plus", "unsure"]),
  realProperty: z.enum(["sole-name", "joint-tenants", "tenants-in-common", "none", "unsure"]),
  disputeRisk: z.enum(["no", "possible", "yes", "unsure"]),
  message: z.string().trim().max(4000).optional().or(z.literal("")),
  sourcePage: z.string().trim().max(200).optional().or(z.literal("")),
  ...attributionFields,
});

export type ExecutorIntakeInput = z.infer<typeof executorSchema>;

export const submitExecutorIntake = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => executorSchema.parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { buildMatterRecord, matterRecordEmailDetails } = await import("@/lib/matter-intake.server");

    const narrative = [
      `Role: ${data.role}`,
      data.deceasedName ? `Deceased: ${data.deceasedName}` : null,
      data.dateOfDeath ? `Date of death: ${data.dateOfDeath}` : null,
      `Place of death: ${data.placeOfDeath}`,
      `Where the executor/applicant lives: ${data.applicantLocation}`,
      `Will status: ${data.willStatus}`,
      data.willLocation ? `Where the original will is held: ${data.willLocation}` : null,
      data.qldAssets?.length ? `Queensland assets: ${data.qldAssets.join(", ")}` : null,
      data.assets?.length ? `Assets: ${data.assets.join(", ")}` : null,
      `Estimated estate value: ${data.estimatedValue}`,
      `Real property held: ${data.realProperty}`,
      `Possible claim or family dispute: ${data.disputeRisk}`,
      data.message ? `\nWhat they told us:\n${data.message}` : null,
    ]
      .filter(Boolean)
      .join("\n");

    const record = await buildMatterRecord({
      name: data.name,
      email: data.email,
      phone: data.phone,
      selectedMatterType: "probate / estate administration",
      message: narrative,
      sourcePage: data.sourcePage,
    });

    const { error } = await supabaseAdmin.from("executor_intakes").insert({
      name: data.name,
      email: data.email,
      phone: data.phone || null,
      role: data.role,
      deceased_name: data.deceasedName || null,
      date_of_death: data.dateOfDeath || null,
      place_of_death: data.placeOfDeath,
      applicant_location: data.applicantLocation,
      qld_assets: data.qldAssets?.length ? data.qldAssets.join(", ") : null,
      will_status: data.willStatus,
      will_location: data.willLocation || null,
      assets: data.assets?.length ? data.assets.join(", ") : null,
      estimated_value: data.estimatedValue,
      real_property: data.realProperty,
      dispute_risk: data.disputeRisk,
      message: data.message || null,
      source_page: data.sourcePage || null,
      ai_summary: record?.summary || null,
      ai_urgency: record?.urgency || null,
      ai_key_dates: record?.keyDates || null,
      ai_next_actions: record?.nextActions || null,
      ai_missing_info: record?.missingInfo || null,
      ai_conflict_names: record?.parties || null,
      ai_processed_at: record ? new Date().toISOString() : null,
      ...attributionColumns(data),
    });

    if (error) {
      console.error("Failed to store executor intake", error);
      throw new Error("We could not send your details. Please call us instead.");
    }

    const { sendLeadToCrm } = await import("@/lib/crm.server");
    await sendLeadToCrm({
      formType: "executor-intake",
      name: data.name,
      email: data.email,
      phone: data.phone,
      matterType: "probate-estate-administration",
      sourcePage: data.sourcePage,
      message: narrative,
      details: {
        role: data.role,
        deceased_name: data.deceasedName,
        date_of_death: data.dateOfDeath,
        place_of_death: data.placeOfDeath,
        applicant_location: data.applicantLocation,
        qld_assets: data.qldAssets?.join(", "),
        will_status: data.willStatus,
        will_location: data.willLocation,
        assets: data.assets?.join(", "),
        estimated_value: data.estimatedValue,
        real_property: data.realProperty,
        dispute_risk: data.disputeRisk,
        ...matterRecordEmailDetails(record),
      },
    });

    return { ok: true } as const;
  });
